import { memo } from 'react';
import { Copy, Loader2, RotateCcw, Save } from 'lucide-react';
import { AdminTranslationLocale, TranslationStatus } from '@/services/adminService';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TranslationFormValue, EntityType } from '@/hooks/useAdminTranslations';

const TRANSLATION_STATUS_OPTIONS: ReadonlyArray<{ value: TranslationStatus; label: string; helper: string; }> = [
  { value: 'mt_seeded', label: 'Machine seeded', helper: 'Automatically generated translation is active.' },
  { value: 'failed', label: 'Needs attention', helper: 'Mark when translation needs manual review or update.' },
];

const getTranslationLabel = (translation: AdminTranslationLocale): string => {
  const providerLabel = translation.provider ? translation.provider.toUpperCase() : '—';
  const updatedAtLabel = translation.updatedAt ? new Date(translation.updatedAt).toLocaleString() : 'Not saved yet';
  return `${providerLabel} · ${updatedAtLabel}`;
};

interface TranslationCardProps {
  entityType: EntityType;
  entityId: string;
  englishDescription: string;
  translation: AdminTranslationLocale;
  formValue: TranslationFormValue;
  isDirty: boolean;
  isSaving: boolean;
  getLocaleLabel: (locale: string) => string;
  onDescriptionChange: (description: string) => void;
  onStatusChange: (status: TranslationStatus) => void;
  onSave: () => void;
  onReset: () => void;
  onCopyEnglish: () => void;
}

const TranslationCard = memo<TranslationCardProps>(({ 
  translation, 
  formValue, 
  isDirty, 
  isSaving, 
  getLocaleLabel, 
  onDescriptionChange, 
  onStatusChange, 
  onSave, 
  onReset, 
  onCopyEnglish 
}) => {
  return (
    <div className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="font-semibold">
            {getLocaleLabel(translation.locale)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {getTranslationLabel(translation)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>
            Status: <strong>{formValue.status}</strong>
          </span>
          {translation.hashCodeDescription && (
            <span className="font-mono">
              Hash: {translation.hashCodeDescription.slice(0, 10)}…
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-[1fr_minmax(200px,220px)] md:items-start">
        <div className="flex flex-col gap-3">
          <Label className="text-sm font-medium">Translated description</Label>
          <textarea
            value={formValue.description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            className="min-h-[140px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
            placeholder={`Enter ${getLocaleLabel(translation.locale)} description…`}
            disabled={isSaving}
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onCopyEnglish} disabled={isSaving}>
              <Copy className="mr-2 h-4 w-4" />
              Copy English
            </Button>
            <Button variant="ghost" size="sm" onClick={onReset} disabled={!isDirty || isSaving}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <Label className="text-sm font-medium">Translation status</Label>
            <Select value={formValue.status} onValueChange={onStatusChange} disabled={isSaving}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSLATION_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-muted-foreground">
              {TRANSLATION_STATUS_OPTIONS.find((option) => option.value === formValue.status)?.helper}
            </p>
          </div>
          <Button onClick={onSave} disabled={!isDirty || isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save translation
          </Button>
        </div>
      </div>
    </div>
  );
});

export default TranslationCard;
